'use client';

import { cn } from '@/lib/utils';
import { Clock, Crown, Sparkles, Zap } from 'lucide-react';
import { FC, useMemo } from 'react';

interface TrialWorkspaceIndicatorProps {
  isTrialActive?: boolean;
  daysRemaining?: number;
  creditsRemaining?: number;
  className?: string;
  variant?: 'badge' | 'icon' | 'full';
  showCredits?: boolean;
}

const TrialWorkspaceIndicator: FC<TrialWorkspaceIndicatorProps> = ({
  isTrialActive = false,
  daysRemaining = 0,
  creditsRemaining = 0,
  className,
  variant = 'badge',
  showCredits = true,
}) => {
  const { gradientClass, textColorClass, bgColorClass } = useMemo(() => {
    const getUrgencyColor = (days: number) => {
      if (days <= 3) return 'from-red-500 to-orange-500';
      if (days <= 7) return 'from-orange-500 to-amber-500';
      return 'from-blue-600 to-blue-700';
    };

    const getUrgencyTextColor = (days: number) => {
      if (days <= 3) return 'text-red-700';
      if (days <= 7) return 'text-orange-700';
      return 'text-blue-700';
    };

    const getUrgencyBgColor = (days: number) => {
      if (days <= 3) return 'bg-red-100 text-red-700 border-red-200';
      if (days <= 7) return 'bg-orange-100 text-orange-700 border-orange-200';
      return 'bg-blue-100 text-blue-700 border-blue-200';
    };

    return {
      gradientClass: getUrgencyColor(daysRemaining),
      textColorClass: getUrgencyTextColor(daysRemaining),
      bgColorClass: getUrgencyBgColor(daysRemaining)
    };
  }, [daysRemaining]);

  if (!isTrialActive) {
    return null;
  }

  if (variant === 'icon') {
    return (
      <div 
        className={cn('relative', className)}
        title={`Professional Trial - ${daysRemaining} days, ${creditsRemaining.toLocaleString()} credits`}
      >
        <div className={`p-1 bg-gradient-to-r ${gradientClass} rounded-full`}>
          <Crown className="h-3 w-3 text-white" />
        </div>
        <Sparkles className="absolute -top-0.5 -right-0.5 h-2 w-2 text-amber-400 animate-pulse" />
      </div>
    );
  }

  if (variant === 'badge') {
    return (
      <div
        className={cn(
          'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors',
          bgColorClass,
          className,
        )}
        title={`Professional Trial - ${daysRemaining} days, ${creditsRemaining.toLocaleString()} credits`}
      >
        <Crown className="h-3 w-3 mr-1" />
        Trial
      </div>
    );
  }

  // Full variant
  return (
    <div
      className={cn(
        `relative overflow-hidden rounded-lg border-0 shadow-sm ${className}`,
      )}
    >
      {/* Background gradient */}
      <div className={`absolute inset-0 bg-gradient-to-r ${gradientClass} opacity-10 pointer-events-none`}></div>

      {/* Sparkle effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <Sparkles className="absolute top-2 right-3 h-3 w-3 text-blue-300 animate-pulse" />
        <Sparkles className="absolute bottom-2 left-3 h-2 w-2 text-blue-300 animate-pulse delay-700" />
      </div>

      <div className="relative p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className={`p-1 bg-gradient-to-r ${gradientClass} rounded-full`}>
              <Crown className="h-3 w-3 text-white" />
            </div>
            <span className={`text-sm font-semibold ${textColorClass}`}>
              Professional Trial
            </span>
          </div>
          <div className="flex items-center gap-1">
            <Clock className={`h-3 w-3 ${textColorClass}`} />
            <span className={`text-xs font-medium ${textColorClass}`}>
              {daysRemaining}d left
            </span>
          </div>
        </div>
        
        {showCredits && (
          <div className="flex items-center gap-2">
            <Zap className={`h-3 w-3 ${textColorClass}`} />
            <span className={`text-xs ${textColorClass}`}>
              <span className="font-semibold">{creditsRemaining.toLocaleString()}</span> credits remaining
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrialWorkspaceIndicator;